import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, Stack, Typography} from 'views/components';
import {Translate, translate} from 'views/module';

import {AssigneeBadge} from '../components';

const PlaybooksView = (props) => {
  const {onDelete, onEdit, playbook, skills} = props;

  const sortedSteps = React.useMemo(() => {
    return [...(playbook?.steps || [])].sort((leftStep, rightStep) => leftStep.day - rightStep.day);
  }, [playbook?.steps]);

  const stepGroups = React.useMemo(() => {
    return sortedSteps.reduce((groups, step) => {
      const currentGroup = groups[groups.length - 1];

      if (currentGroup?.day === step.day) {
        currentGroup.steps.push(step);
        return groups;
      }

      return [
        ...groups,
        {
          day: step.day,
          steps: [step],
        },
      ];
    }, []);
  }, [sortedSteps]);

  const skillsByRn = React.useMemo(() => {
    return new Map((Array.isArray(skills) ? skills : []).map((skill) => [skill.skillRn, skill]));
  }, [skills]);

  const renderStepPrompt = React.useCallback((step) => {
    const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const skill = skillsByRn.get(step.skillRn);
    const skillName = skill?.name;

    if (!skillName || !step.prompt?.includes(`/${skillName}`)) {
      return `\u2022 ${step.prompt}`;
    }

    const skillMatcher = new RegExp(`/${escapeRegExp(skillName)}(?=\\s|$)`);
    const skillMatch = skillMatcher.exec(step.prompt);

    if (!skillMatch) {
      return `\u2022 ${step.prompt}`;
    }

    const prefix = step.prompt.slice(0, skillMatch.index);
    const matchedText = skillMatch[0];
    const suffix = step.prompt.slice(skillMatch.index + matchedText.length);

    return (
      <>
        {'\u2022'} {prefix}
        <Typography
          component='button'
          onClick={() => window.open(`/skills?selectedSkillRn=${encodeURIComponent(step.skillRn)}`)}
          sx={{
            'background': 'none',
            'border': 'none',
            'color': '#DA7955',
            'cursor': 'pointer',
            'display': 'inline',
            'font': 'inherit',
            'lineHeight': 'inherit',
            'p': 0,
            'verticalAlign': 'baseline',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          {matchedText}
        </Typography>
        {suffix}
      </>
    );
  }, [skillsByRn]);

  if (!playbook) {
    return (
      <Box
        sx={{
          width: '100%',
          p: '40px',
          boxSizing: 'border-box',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            color: '#A8A49E',
            lineHeight: '150%',
          }}
        >
          <Translate expression='playbooks.View.Empty' />
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        p: '40px',
        boxSizing: 'border-box',
        backgroundColor: '#FFFFFF',
      }}
    >
      <Stack
        sx={{
          gap: '10px',
        }}
      >
        <Stack
          direction='row'
          alignItems='flex-start'
          justifyContent='space-between'
          sx={{
            gap: '24px',
            flexWrap: 'wrap',
          }}
        >
          <Stack
            sx={{
              gap: '12px',
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              sx={{
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontStyle: 'Regular',
                  fontSize: '22px',
                  color: '#1A1916',
                  lineHeight: '100%',
                }}
              >
                 /{playbook.title}
              </Typography>
            </Stack>
            <Typography
              sx={{
                fontWeight: 400,
                fontStyle: 'Regular',
                fontSize: '14px',
                color: '#A8A49E',
                lineHeight: '100%',
              }}
            >
              {playbook.groupName}
            </Typography>
          </Stack>
          <Stack
            direction='row'
            sx={{
              gap: '8px',
            }}
          >
            <Button
              onClick={onEdit}
              disableElevation
              sx={{
                'minWidth': '0',
                'borderRadius': '7px',
                'px': '12px',
                'py': '8px',
                'fontWeight': 500,
                'fontStyle': 'Medium',
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#FFFFFF',
                'backgroundColor': '#DA7955',
                '&:hover': {
                  backgroundColor: '#DA7955',
                  boxShadow: 'none',
                },
              }}
            >
              <Translate expression='playbooks.View.Edit' />
            </Button>
            <Button
              onClick={onDelete}
              disableElevation
              sx={{
                'minWidth': '0',
                'borderRadius': '7px',
                'px': '12px',
                'py': '8px',
                'fontWeight': 500,
                'fontStyle': 'Medium',
                'fontSize': '12px',
                'lineHeight': '100%',
                'textTransform': 'none',
                'color': '#6B6760',
                'border': '1px solid #D8D3CC',
                'backgroundColor': 'transparent',
                '&:hover': {
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                },
              }}
            >
              <Translate expression='playbooks.View.Delete' />
            </Button>
          </Stack>
        </Stack>
        <Stack gap={2}>
          <Box
            sx={{
              border: '1px solid',
              borderColor: '#E8E4DE',
              borderRadius: '12px',
              px: '14px',
              py: '12px',
              backgroundColor: '#FBFBFB',
            }}
          >
            <Stack
              sx={{
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#A8A49E',
                  lineHeight: '100%',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='playbooks.View.Description' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#1A1916',
                  lineHeight: '150%',
                }}
              >
                {
                  playbook.description.blob ? `"${playbook.description.blob}"` : translate('playbooks.View.NoDescription')
                }
              </Typography>
            </Stack>
          </Box>
          <Box>
            <Stack
              sx={{
                gap: '10px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#A8A49E',
                  lineHeight: '100%',
                  textTransform: 'uppercase',
                }}
              >
                <Translate expression='playbooks.View.Steps' />
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {
                  stepGroups.map((group) => {
                    return (
                      <Box
                        sx={{
                          border: '1px solid #E8E4DE',
                          borderRadius: '12px',
                          backgroundColor: 'transparent',
                          px: '12px',
                          py: '10px',
                        }}
                        key={`day-group-${group.day}`}
                      >
                        <Stack
                          sx={{
                            gap: '8px',
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 500,
                              color: '#A8A49E',
                              lineHeight: '150%',
                              fontSize: '13px',
                            }}
                          >
                            <Translate expression='playbooks.New.StepPreview.Day' substitute={{day: group.day}} />
                          </Typography>
                          {
                            group.steps.map((step, index) => (
                              <Stack
                                direction='row'
                                alignItems='flex-start'
                                sx={{
                                  gap: '8px',
                                }}
                                key={step.stepRn || `${step.prompt || 'step'}-${index}`}
                              >
                                <Box
                                  sx={{
                                    width: '56px',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                  }}
                                >
                                  {
                                    Boolean(step.skillRn) &&
                                    <Box
                                      component='img'
                                      src={step.skillRn ? `icons/zap.svg` : undefined}
                                      title='Skill'
                                    />
                                  }
                                </Box>
                                <Stack
                                  sx={{
                                    flex: 1,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: '13px',
                                      fontWeight: 500,
                                      color: '#1A1916',
                                      lineHeight: '150%',
                                    }}
                                  >
                                    {renderStepPrompt(step)}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction='row'
                                  sx={{
                                    alignItems: 'center',
                                    flexShrink: 0,
                                  }}
                                >
                                  {
                                    ((step.ownerRns && step.ownerRns.length > 0) ? step.ownerRns : [null]).map((ownerRn, ownerIndex) => (
                                      <AssigneeBadge
                                        key={`${step.stepRn || index}-${ownerRn || 'unassigned'}`}
                                        interactive={false}
                                        user={ownerRn ? {userRn: ownerRn} : {}}
                                        sx={{
                                          width: '24px',
                                          height: '24px',
                                          position: 'relative',
                                          zIndex: ownerIndex + 1,
                                          fontSize: '11px',
                                          outline: '2px solid',
                                          outlineColor: '#FFFFFF',
                                          ml: ownerIndex === 0 ? 0 : '-6px',
                                        }}
                                      />
                                    ))
                                  }
                                </Stack>
                              </Stack>
                            ))
                          }
                        </Stack>
                      </Box>
                    );
                  })
                }
              </Box>
            </Stack>
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
};

PlaybooksView.propTypes = {
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  playbook: PropTypes.shape({
    description: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        blob: PropTypes.string,
        editorType: PropTypes.string,
      }),
    ]),
    groupName: PropTypes.string,
    playbookRn: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      day: PropTypes.number,
      ownerRns: PropTypes.arrayOf(PropTypes.string),
      prompt: PropTypes.string,
      skillRn: PropTypes.string,
      stepRn: PropTypes.string,
    })),
    title: PropTypes.string,
  }),
  skills: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    skillRn: PropTypes.string,
  })).isRequired,
};

const mapStateToProps = (state) => ({
  skills: state.skills.allSkills,
});

export default connect(mapStateToProps, dispatch)(React.memo(PlaybooksView));
